import { useCallback, useEffect, useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';

const normalizeOrder = (storedOrder, defaultOrder) => {
	const safeStoredOrder = Array.isArray(storedOrder) ? storedOrder : [];

	return [
		...safeStoredOrder.filter((key) => defaultOrder.includes(key)),
		...defaultOrder.filter((key) => !safeStoredOrder.includes(key)),
	];
};

const normalizeVisibility = (storedVisibility, defaultVisibility) => {
	const safeStoredVisibility =
		storedVisibility && typeof storedVisibility === 'object'
			? storedVisibility
			: {};

	return Object.keys(defaultVisibility).reduce(
		(acc, key) => ({
			...acc,
			[key]:
				typeof safeStoredVisibility[key] === 'boolean'
					? safeStoredVisibility[key]
					: defaultVisibility[key],
		}),
		{}
	);
};

const areArraysEqual = (a, b) => {
	if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
		return false;
	}

	return a.every((item, index) => item === b[index]);
};

const areObjectsEqual = (a, b) => {
	if (!a || !b) {
		return false;
	}

	const keysA = Object.keys(a);
	const keysB = Object.keys(b);

	if (keysA.length !== keysB.length) {
		return false;
	}

	return keysA.every((key) => a[key] === b[key]);
};

function usePersistentTableColumns({
	storageKeyPrefix,
	defaultColumnOrder,
	defaultColumnVisibility,
}) {
	const [storedColumnOrder, setStoredColumnOrder] = useLocalStorageState(
		`${storageKeyPrefix}_column_order`,
		{
			defaultValue: defaultColumnOrder,
		}
	);

	const [storedColumnVisibility, setStoredColumnVisibility] =
		useLocalStorageState(`${storageKeyPrefix}_column_visibility`, {
			defaultValue: defaultColumnVisibility,
		});

	const columnOrder = useMemo(
		() => normalizeOrder(storedColumnOrder, defaultColumnOrder),
		[storedColumnOrder, defaultColumnOrder]
	);

	const columnVisibility = useMemo(
		() => normalizeVisibility(storedColumnVisibility, defaultColumnVisibility),
		[storedColumnVisibility, defaultColumnVisibility]
	);

	useEffect(() => {
		if (!areArraysEqual(storedColumnOrder, columnOrder)) {
			setStoredColumnOrder(columnOrder);
		}
	}, [storedColumnOrder, columnOrder, setStoredColumnOrder]);

	useEffect(() => {
		if (!areObjectsEqual(storedColumnVisibility, columnVisibility)) {
			setStoredColumnVisibility(columnVisibility);
		}
	}, [storedColumnVisibility, columnVisibility, setStoredColumnVisibility]);

	const resetColumns = useCallback(() => {
		setStoredColumnOrder(defaultColumnOrder);
		setStoredColumnVisibility(defaultColumnVisibility);
	}, [
		defaultColumnOrder,
		defaultColumnVisibility,
		setStoredColumnOrder,
		setStoredColumnVisibility,
	]);

	return {
		columnOrder,
		setColumnOrder: setStoredColumnOrder,
		columnVisibility,
		setColumnVisibility: setStoredColumnVisibility,
		resetColumns,
	};
}

export default usePersistentTableColumns;
